import React, {ChangeEvent} from "react";
import {useNavigate, useParams} from "react-router-dom";

import DropDownMenu, {IChoice, findOptionFromList} from "@components/inputs/DropDownMenu";
import Label from "@components/inputs/text/Label";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import {NavBar} from "@components/navigation/NavBar/NavBar";
import LargeTextbox from "@components/inputs/text/LargeTextbox";
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";

import {ExchangeReasonAction, ExchangeReasonState, ExchangeTypes} from "./exchangeReasonReducer";
import InfoBox from "../../components/cards/messageBox/InfoBox";
import {SelectedSubscriptionState} from "@views/VehicleExchange/selectedSubscriptionReducer";
import {PlanOptions} from "@utils/planOptions";

// Map the value to the label. Value is stored in the reducer state
export enum ExchangeOptions {
    Permanent = "Permanent Vehicle Exchange",
    Temporary = "Temporary Vehicle Exchange",
}

export enum ExchangeReasonOptions {
    AccidentReplacement = "Accident Replacement",
    DefleetingVehicle = "Defleeting Vehicle",
    ReturningToContractedVehicle = "Returning to Contracted Vehicle",
    TotalLossReplacement = "Total Loss Replacement",
    WarrantyReplacement = "Warranty Replacement",
    SoldAsPreloved = "Sold as Preloved",
    OutrightPurchase = "Outright Purchase",
    PCOExpired = "PCO Expired",
    InterstateTransfer = "Interstate Transfer"
}

type Props = {
    navigateTarget: string;
    exchangeReasonState: ExchangeReasonState;
    exchangeReasonDispatch: React.Dispatch<ExchangeReasonAction>;
    selectedSubscriptionState: SelectedSubscriptionState;
};

export default function ExchangeReason({
                                           navigateTarget,
                                           exchangeReasonState,
                                           exchangeReasonDispatch,
                                           selectedSubscriptionState,
                                       }: Props) {
    const {country} = useParams();
    const navigate = useNavigate();

    const componentName = "Exchange reason";

    const shouldExchangeOptionEnable = (selectedExchangeOption: string, selectedPlan: string | null): boolean => {
        // Uses the selected subscription to determine if the given exchange option is a valid choice (enabled)
        // Designed to allow by default, disable for specific required plans (Flexi + RAV)
        if (selectedExchangeOption === "Temporary"
            && (selectedPlan === PlanOptions.SplendFlexi
                || selectedPlan === PlanOptions.UKFlexi
                || selectedPlan == PlanOptions.RAVCreditHire
                || selectedPlan == PlanOptions.RAVSharecover
                || selectedPlan == PlanOptions.RAVShortTermRental)) {
            return false;
        } else {
            return true;
        }
    };

    const exchangeOptions: IChoice[] = Object.entries(ExchangeOptions).map(([value, label]) => ({
        value,
        label,
        enabled: shouldExchangeOptionEnable(value, selectedSubscriptionState.selectedPlan),
    }));


    const exchangeReasons: IChoice[] = Object.entries(ExchangeReasonOptions).map(([value, label]) => ({
        value,
        label,
        enabled: true,
    })).filter(opt => {
        // AU specific exchange reason
        if (opt.label === ExchangeReasonOptions.InterstateTransfer) {
            return country === "AU";
        }
        return true;
    });

    const selectedExchangeType: IChoice =
        findOptionFromList(exchangeReasonState.exchangeType, exchangeOptions);

    const selectedExchangeReason: IChoice =
        findOptionFromList(exchangeReasonState.exchangeReason, exchangeReasons);

    const comments = exchangeReasonState.comments || "";

    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        exchangeReasonDispatch({type: "USER_TYPES_COMMENTS", payload: value});
    };

    const onSelectExchangeType = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value as ExchangeTypes;
        exchangeReasonDispatch({type: "USER_SELECTS_EXCHANGE_TYPE", payload: value});
    };

    const onSelectExchangeReason = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        exchangeReasonDispatch({type: "USER_SELECTS_EXCHANGE_REASON", payload: value});
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        navigate(navigateTarget);
    };

    return (
        <>
            <NavBar pageTitle="Exchange reason" backAction={undefined}/>
            <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}></div>
            <InfoBox
                message={
                    <>
                        A <b>temporary exchange</b> means the current vehicle will need to be returned to the customer
                        in the future.
                        <br/><br/>
                        A <b>permanent exchange</b> means the current vehicle will not be returned to the customer in
                        the future
                    </>
                }
            />
            <form style={{margin: "2.5vh 2.5vw"}} onSubmit={handleSubmit}>
                <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                    <Label text="Please select an exchange type:"/>
                    <DropDownMenu
                        menuName="exchangeType"
                        defaultVal={selectedExchangeType}
                        required
                        choices={exchangeOptions}
                        onSelect={onSelectExchangeType}
                        testId="type-dropdown"
                    />
                </div>

                <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                    <Label
                        text={`Why is this customer being ${exchangeReasonState.exchangeType === 'Permanent' ? 'permanently' : 'temporarily'} exchanged out of their original vehicle?`}/>
                    <DropDownMenu
                        menuName="exchangeReason"
                        defaultVal={selectedExchangeReason}
                        required
                        choices={exchangeReasons}
                        onSelect={onSelectExchangeReason}
                        testId="reason-dropdown"
                    />
                </div>

                <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                    <Label text="Additional Comments:"/>
                    <LargeTextbox value={comments} onChange={handleCommentChange}/>
                </div>

                <SubmitBtn text="Next" breadcrumbObject={breadcrumbObject} componentName={componentName}/>
            </form>
        </>
    );
}
